import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { TspCard, TspTabs, TspEmptyState } from "../../../components/primitives";
import MediaThumb from "../../media-gallery/components/MediaThumb";
import MediaViewerModal from "../../media-gallery/components/MediaViewerModal";

// Filter tab label -> media `media_type` it shows (null = everything).
const FILTERS = { All: null, Photos: "IMAGE", Videos: "VIDEO" };
const PAGE_SIZE = 8;

// `gallery` is GET /api/tsp/stats#media_gallery (null without tsp.media.view).
export default function MediaGallerySection({ gallery, subject = "this journey" }) {
  const [filter, setFilter] = useState("All");
  const [page, setPage] = useState(1);
  const [viewerId, setViewerId] = useState(null);

  if (!gallery) {
    return (
      <TspCard title="Media Gallery" className="tsp-title-18">
        <TspEmptyState message="You do not have permission to view media." />
      </TspCard>
    );
  }

  const type = FILTERS[filter];
  const allItems = type ? (gallery.items || []).filter((item) => item.media_type === type) : gallery.items || [];
  const totalItems = allItems.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const currentPage = Math.min(Math.max(1, page), totalPages);

  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const pagedItems = allItems.slice(startIndex, startIndex + PAGE_SIZE);

  const journeyId = gallery.journey_id;
  const viewerIndex = viewerId ? allItems.findIndex((item) => item.id === viewerId) : -1;

  const handleFilterChange = (nextFilter) => {
    setFilter(nextFilter);
    setPage(1);
  };

  return (
    <TspCard
      title="Media Gallery"
      className="tsp-title-18"
      action={
        <span className="tsp-card-action-group">
          <span className="tsp-status-pill tsp-pill-blue">{gallery.photos_count || 0} Photos</span>
          <span className="tsp-status-pill tsp-pill-purple">{gallery.videos_count || 0} Videos</span>
          <Link to={journeyId ? `/tsp/media-gallery?journey=${journeyId}` : "/tsp/media-gallery"} className="tsp-view-all-link">
            View Gallery
          </Link>
        </span>
      }
    >
      {(gallery.photos_count === 0 && gallery.videos_count === 0) || allItems.length === 0 ? (
        gallery.photos_count === 0 && gallery.videos_count === 0 ? (
          <TspEmptyState message={`No media uploaded on ${subject} yet.`} />
        ) : (
          <>
            <TspTabs tabs={Object.keys(FILTERS)} active={filter} onChange={handleFilterChange} className="tsp-tabs-compact" />
            <p className="tsp-muted-text" style={{ padding: "24px 0", textAlign: "center" }}>
              No {filter.toLowerCase()} available.
            </p>
          </>
        )
      ) : (
        <>
          <TspTabs tabs={Object.keys(FILTERS)} active={filter} onChange={handleFilterChange} className="tsp-tabs-compact" />
          <div className="tsp-media-grid">
            {pagedItems.map((item) => (
              <div key={item.id} className="tsp-media-thumb">
                <button type="button" className="tsp-media-open" onClick={() => setViewerId(item.id)} aria-label={`Open ${item.caption || "media"}`}>
                  <MediaThumb item={item} />
                </button>
                <div className="tsp-media-thumb-footer">
                  <span>{item.caption || "Activity"}</span>
                </div>
              </div>
            ))}
            {gallery.more_count > 0 && currentPage === totalPages && (
              <Link to={journeyId ? `/tsp/media-gallery?journey=${journeyId}` : "/tsp/media-gallery"} className="tsp-media-thumb tsp-media-thumb-more">
                <span>+{gallery.more_count}</span>
                <span>more</span>
              </Link>
            )}
          </div>

          {totalPages > 1 && (
            <div className="tsp-media-pagination">
              <span className="tsp-media-pagination-info">
                Showing {startIndex + 1}–{Math.min(startIndex + PAGE_SIZE, totalItems)} of {totalItems}
              </span>
              <div className="tsp-media-pagination-actions">
                <button
                  type="button"
                  className="tsp-btn tsp-btn-outline tsp-btn-sm"
                  disabled={currentPage <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  aria-label="Previous page"
                >
                  <ChevronLeft size={16} aria-hidden="true" /> Previous
                </button>
                <span className="tsp-media-page-indicator">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  type="button"
                  className="tsp-btn tsp-btn-outline tsp-btn-sm"
                  disabled={currentPage >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  aria-label="Next page"
                >
                  Next <ChevronRight size={16} aria-hidden="true" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
      {viewerIndex >= 0 && (
        <MediaViewerModal items={allItems} index={viewerIndex} onIndexChange={(next) => setViewerId(allItems[next].id)} onClose={() => setViewerId(null)} canRemove={false} showDetails={false} />
      )}
    </TspCard>
  );
}
